import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SITE } from "@/lib/site";
import { Phone, Mail, MessageCircle, Copy, CheckCircle2, FileDown, Clock, Users } from "lucide-react";
import PageSeo from "@/components/PageSeo";

const initial = {
  name: "",
  email: "",
  phone: "",
  date: "",
  time: "19:30",
  party_size: 2,
  occasion: "",
  notes: "",
};

export default function Reservations() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [avail, setAvail] = useState(null); // {available, reason, remaining, cap, blocked_reason}
  const [checkingAvail, setCheckingAvail] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Debounced availability check whenever date/time/party_size are filled
  const checkAvailability = async (date, time, party_size) => {
    if (!date || !time || !party_size) { setAvail(null); return; }
    setCheckingAvail(true);
    try {
      const { data } = await api.get("/reservations/availability", { params: { date, time, party_size } });
      setAvail(data);
    } catch { setAvail(null); }
    finally { setCheckingAvail(false); }
  };

  // Fire availability check when the three critical fields change
  const updateWithCheck = (k) => (e) => {
    const v = e.target.value;
    setForm((f) => {
      const next = { ...f, [k]: v };
      if (["date", "time", "party_size"].includes(k)) {
        // Fire and forget — debounced-ish via requestAnimationFrame
        requestAnimationFrame(() => checkAvailability(next.date, next.time, Number(next.party_size) || 0));
      }
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (avail && !avail.available) {
      toast.error(avail.reason === "blocked"
        ? `That slot is not available (${avail.blocked_reason || "closed"}). Please pick another time.`
        : `Only ${avail.remaining} seats left in that slot. Please reduce party size or pick another time.`);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/reservations", {
        ...form,
        party_size: Number(form.party_size),
      });
      toast.success("Reservation request received. We'll confirm on WhatsApp.");
      setConfirmation(data);
      setForm(initial);
      setAvail(null);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error("Too many requests — please wait a few minutes and try again.");
      } else {
        const msg = err?.response?.data?.detail || "Something went wrong. Please try again or WhatsApp us.";
        toast.error(typeof msg === "string" ? msg : "Please check the form and try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="reservations-page" className="pt-32 pb-24">
      <PageSeo
        title="Reservations — Book a Table at The Barracks Bistro"
        description="Reserve a table for lunch, dinner, or a special occasion at Wellington's lakeside multi-cuisine restaurant. Confirmation on WhatsApp within an hour."
        path="/reservations"
      />
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="eyebrow">Reservations</div>
        <h1 className="mt-3 text-5xl sm:text-6xl max-w-3xl leading-[1]">
          Hold a table. <span className="italic text-[hsl(var(--brass))]">Come hungry.</span>
        </h1>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-5 gap-14">
          <form
            data-testid="reservation-form"
            onSubmit={submit}
            className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-5"
          >
            {confirmation && (
              <div data-testid="reservation-confirmation" className="md:col-span-2 border border-[hsl(var(--brass))] bg-[hsl(var(--brass)/0.08)] p-5">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={18} className="text-[hsl(var(--brass-2))]" />
                  <div className="font-semibold">We&apos;ve received your request</div>
                </div>
                <p className="mt-2 text-sm text-[hsl(var(--ink-soft))]">
                  You&apos;ll get a WhatsApp confirmation from us shortly. If plans change, please
                  call <a href="tel:+918220410761" className="text-[hsl(var(--forest))] font-semibold">+91 82204 10761</a> or
                  WhatsApp us — our team will take care of any changes or cancellations.
                </p>
                <div data-testid="dining-time-policy" className="mt-4 pt-4 border-t border-[hsl(var(--brass)/0.35)]">
                  <div className="flex items-center gap-2 text-sm font-semibold text-[hsl(var(--ink))]">
                    <Clock size={14} className="text-[hsl(var(--brass-2))]" />
                    Dining time
                  </div>
                  <ul className="mt-1.5 text-sm text-[hsl(var(--ink-soft))] space-y-0.5 pl-6 list-disc marker:text-[hsl(var(--brass-2))]">
                    <li>90 mins for starters</li>
                    <li>90 mins for main course</li>
                  </ul>
                  <p className="mt-1.5 text-xs text-[hsl(var(--ink-soft))] pl-6">
                    Any starters ordered after 90 mins will be charged à la carte.
                  </p>
                </div>
                <div data-testid="advance-policy" className="mt-3 pt-3 border-t border-[hsl(var(--brass)/0.35)]">
                  <div className="flex items-center gap-2 text-sm font-semibold text-[hsl(var(--ink))]">
                    <Users size={14} className="text-[hsl(var(--brass-2))]" />
                    Parties above 20 guests
                  </div>
                  <p className="mt-1 text-sm text-[hsl(var(--ink-soft))] pl-6">
                    A <span className="font-semibold text-[hsl(var(--ink))]">50% advance</span> is required to confirm the booking. Our team will share payment details with you separately.
                  </p>
                </div>
              </div>
            )}
            <Field label="Full name" required>
              <input required data-testid="res-name" value={form.name} onChange={update("name")} className="tbb-input" />
            </Field>
            <Field label="Phone">
              <input required data-testid="res-phone" value={form.phone} onChange={update("phone")} className="tbb-input" />
            </Field>
            <Field label="Email" full>
              <input required type="email" data-testid="res-email" value={form.email} onChange={update("email")} className="tbb-input" />
            </Field>
            <Field label="Date">
              <input required type="date" data-testid="res-date" value={form.date} onChange={updateWithCheck("date")} className="tbb-input" />
            </Field>
            <Field label="Time">
              <input required type="time" data-testid="res-time" value={form.time} onChange={updateWithCheck("time")} className="tbb-input" />
            </Field>
            <Field label="Party size">
              <input required type="number" min="1" max="40" data-testid="res-party" value={form.party_size} onChange={updateWithCheck("party_size")} className="tbb-input" />
            </Field>
            {avail && (
              <div data-testid="res-availability" className={`md:col-span-2 -mt-2 text-sm px-3 py-2 border ${
                avail.available
                  ? "border-[hsl(var(--forest))] bg-[hsl(148_40%_96%)] text-[hsl(var(--forest))]"
                  : "border-[hsl(6_62%_45%)] bg-[hsl(6_60%_97%)] text-[hsl(6_62%_45%)]"
              }`}>
                {checkingAvail ? "Checking availability…" : avail.available
                  ? `✓ Available — ${avail.remaining} of ${avail.cap} seats free at this slot.`
                  : (avail.reason === "blocked"
                    ? `✕ Not available — ${avail.blocked_reason || "closed"}. Please pick another time.`
                    : `✕ This slot is nearly full — only ${avail.remaining} seat(s) left. Please pick another time or reduce party size.`)}
              </div>
            )}
            <Field label="Occasion (optional)">
              <input data-testid="res-occasion" value={form.occasion} onChange={update("occasion")} placeholder="Anniversary, birthday..." className="tbb-input" />
            </Field>
            <Field label="Notes (allergies, seating)" full>
              <textarea data-testid="res-notes" rows={3} value={form.notes} onChange={update("notes")} className="tbb-input resize-none" />
            </Field>
            <div className="md:col-span-2 flex flex-wrap gap-4 items-center pt-2">
              <button data-testid="res-submit" type="submit" disabled={submitting} className="btn-brass disabled:opacity-60">
                {submitting ? "Sending..." : "Request table"}
              </button>
              <a
                data-testid="res-download-form"
                href={`${process.env.REACT_APP_BACKEND_URL}/api/forms/booking-request.pdf`}
                download
                className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-[hsl(var(--brass))] text-[hsl(var(--brass-2))] hover:bg-[hsl(var(--brass))] hover:text-[hsl(var(--bg))] transition-colors"
              >
                <FileDown size={14} /> Download blank form (PDF)
              </a>
              <span className="text-sm text-[hsl(var(--ink-soft))]">
                We confirm every booking on WhatsApp within an hour.
              </span>
            </div>
            <div data-testid="dining-time-policy-form" className="md:col-span-2 mt-2 space-y-1.5">
              <div className="flex items-start gap-2 text-xs text-[hsl(var(--ink-soft))] border-l-2 border-[hsl(var(--brass))] pl-3 py-1">
                <Clock size={13} className="mt-0.5 text-[hsl(var(--brass-2))] flex-shrink-0" />
                <span>
                  <span className="font-semibold text-[hsl(var(--ink))]">Dining time:</span>{" "}
                  90 mins for starters &amp; 90 mins for main course. Any starters ordered after 90 mins will be charged à la carte.
                </span>
              </div>
              <div data-testid="advance-policy-form" className="flex items-start gap-2 text-xs text-[hsl(var(--ink-soft))] border-l-2 border-[hsl(var(--brass))] pl-3 py-1">
                <Users size={13} className="mt-0.5 text-[hsl(var(--brass-2))] flex-shrink-0" />
                <span>
                  <span className="font-semibold text-[hsl(var(--ink))]">Parties above 20 guests:</span>{" "}
                  A 50% advance is required to confirm the booking.
                </span>
              </div>
            </div>
          </form>

          <aside className="lg:col-span-2 lg:pl-6 lg:border-l lg:border-[hsl(var(--rule))]">
            <div className="eyebrow">Rather just call?</div>
            <ul className="mt-4 space-y-4">
              <li>
                <a href={SITE.whatsapp} target="_blank" rel="noreferrer" data-testid="res-whatsapp" className="flex items-center gap-3 text-lg hover:text-[hsl(var(--brass-2))]">
                  <MessageCircle size={18} className="text-[hsl(var(--brass))]" />
                  WhatsApp — {SITE.phonePrimary}
                </a>
              </li>
              <li>
                <a href={`tel:${SITE.phonePrimary.replace(/\s/g, "")}`} data-testid="res-phone-primary" className="flex items-center gap-3 text-lg hover:text-[hsl(var(--brass-2))]">
                  <Phone size={18} className="text-[hsl(var(--brass))]" />
                  {SITE.phonePrimary}
                </a>
              </li>
              <li>
                <a href={`tel:${SITE.phoneSecondary.replace(/\s/g, "")}`} data-testid="res-phone-secondary" className="flex items-center gap-3 text-lg hover:text-[hsl(var(--brass-2))]">
                  <Phone size={18} className="text-[hsl(var(--brass))]" />
                  {SITE.phoneSecondary}
                </a>
              </li>
              <li>
                <a href={`mailto:${SITE.email}`} data-testid="res-email-link" className="flex items-center gap-3 text-lg hover:text-[hsl(var(--brass-2))]">
                  <Mail size={18} className="text-[hsl(var(--brass))]" />
                  {SITE.email}
                </a>
              </li>
            </ul>

            <div className="rule-brass my-8" />

            <div className="eyebrow">Hours</div>
            <ul className="mt-4 text-sm space-y-1.5">
              {SITE.hours.map((h) => (
                <li key={h.day} className="flex justify-between gap-6">
                  <span>{h.day}</span>
                  <span className="text-[hsl(var(--ink-soft))]">{h.time}</span>
                </li>
              ))}
            </ul>
          </aside>
        </div>
      </div>

      <style>{`
        .tbb-input {
          width: 100%;
          background: hsl(var(--bg-2));
          border: 1px solid hsl(var(--rule));
          border-radius: 4px;
          padding: 0.85rem 1rem;
          font-family: inherit;
          font-size: 0.95rem;
          color: hsl(var(--ink));
          transition: border-color 200ms ease, background 200ms ease;
        }
        .tbb-input:focus {
          outline: none;
          border-color: hsl(var(--forest));
          background: hsl(var(--bg));
        }
      `}</style>
    </div>
  );
}

function Field({ label, children, full, required }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <span className="block text-xs uppercase tracking-[0.18em] text-[hsl(var(--ink-soft))] mb-2">
        {label}{required ? " *" : ""}
      </span>
      {children}
    </label>
  );
}
